import classNames from "classnames";
import { FC } from "react";
import useIdentity from "../../hooks/useIdentity";
import { useI18n } from "../../hooks/useTranslations";
import { ProcessingRequestStatus, ProcessingRequestStatuses } from "../../http/processingRequest/types";
import { ProcessingRequestHistory } from "../../http/processingRequestHistory/types";
import { formatHours } from "../../types/utils";
import SvgNavigateNext from "../icons/NavigateNext";
import Inline from "../ui/Inline";
import { Status } from "../ui/Status";
import UserInitials from "../UserInitials";
import styles from "./HistoryMessage.module.css";

type HistoryMessageProps = {
  reuqestHistory: ProcessingRequestHistory;
  showUser: boolean;
};

type StatusAction =
  | "Request created"
  | "Request accepted"
  | "Offer sent"
  | "Offer approved"
  | "Work started"
  | "Work completed"
  | "Offer refused"
  | "Request discarded";

const HistoryMessage: FC<HistoryMessageProps> = ({ reuqestHistory, showUser }) => {
  const i18n = useI18n();

  const StatusActions = {
    Open: i18n.t.processingRequests.status.executedActions.requestCreated as StatusAction,
    Accepted: i18n.t.processingRequests.status.executedActions.requestAccepted as StatusAction,
    WaitingConfirmation: i18n.t.processingRequests.status.executedActions.offerSent as StatusAction,
    OfferApproved: i18n.t.processingRequests.status.executedActions.offerApproved as StatusAction,
    InProgress: i18n.t.processingRequests.status.executedActions.workStarted as StatusAction,
    Closed: i18n.t.processingRequests.status.executedActions.workCompleted as StatusAction,
    Refused: i18n.t.processingRequests.status.executedActions.offerRefused as StatusAction,
    Discarded: i18n.t.processingRequests.status.executedActions.requestDiscarded as StatusAction,
  };

  const getRequestStatusAction = (status: ProcessingRequestStatus): string => {
    switch (status) {
      case ProcessingRequestStatuses.Open:
        return StatusActions.Open;
      case ProcessingRequestStatuses.Accepted:
        return StatusActions.Accepted;
      case ProcessingRequestStatuses.WaitingConfirmation:
        return StatusActions.WaitingConfirmation;
      case ProcessingRequestStatuses.OfferApproved:
        return StatusActions.OfferApproved;
      case ProcessingRequestStatuses.InProgress:
        return StatusActions.InProgress;
      case ProcessingRequestStatuses.Closed:
        return StatusActions.Closed;
      case ProcessingRequestStatuses.Refused:
        return StatusActions.Refused;
      case ProcessingRequestStatuses.Discarded:
        return StatusActions.Discarded;
      default:
        return status;
    }
  };

  const identity = useIdentity();

  const isOwnMessage = reuqestHistory.user.id === identity.id;

  const statusChanged = !!reuqestHistory.newStatus && !!reuqestHistory.oldStatus;

  return (
    <Inline
      className={classNames(styles.messageRow, {
        [styles.ownMessage]: isOwnMessage,
      })}
      columnGap={1}
      alignItems="center"
    >
      <div className={classNames([!showUser ? styles.hideUser : undefined])}>
        {
          <UserInitials
            firstName={reuqestHistory.user.firstName ?? ""}
            lastName={reuqestHistory.user.lastName ?? ""}
            userId={reuqestHistory.user.id}
          />
        }
      </div>
      <div className={styles.messageColumn}>
        <div className={styles.message}>
          {!statusChanged && (
            <div className={styles.userName}>
              <b>{showUser && reuqestHistory.user.firstName + " " + reuqestHistory.user.lastName}</b>
            </div>
          )}
          {reuqestHistory.newStatus && statusChanged ? (
            <div className={styles.alignContents}>
              <Inline alignItems="center" className={styles.messageRow}>
                <b>{getRequestStatusAction(reuqestHistory.newStatus)}</b> <SvgNavigateNext className={styles.arrow} />
                <Status status={reuqestHistory.newStatus} />
              </Inline>
              {reuqestHistory.notes}
            </div>
          ) : (
            reuqestHistory.notes
          )}
          <div className={styles.date}>{formatHours(reuqestHistory.timestamp, i18n.language)}</div>
        </div>
      </div>
    </Inline>
  );
};

export default HistoryMessage;
